/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { useDashboardStats } from "../../store/useDashboardStats";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import { getWeekRange } from "../../services/dateService";
import Chart from "react-apexcharts";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import dayjs from "dayjs";

export const StatiegeldDropsLocationCount = React.memo(() => {
  /** Refs */
  const snapshotRef = useRef(null);
  /** Hooks */
  const { dropsLocationStats, getDropsStatsByLocation } = useDashboardStats();
  const [, takeScreenshot] = useScreenshot();
  /** State */
  const [period, setPeriod] = useState<"weekly" | "monthly" | "yearly">(
    "monthly"
  );
  const [date, setDate] = useState(new Date());
  /** Constants */
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();

  /** Callbacks */
  const { startOfWeek, endOfWeek } = getWeekRange(date);

  const getLastWeek = () => setDate(new Date(date.setDate(date.getDate() - 7)));
  const getNextWeek = () => setDate(new Date(date.setDate(date.getDate() + 7)));

  const downloadScreenshot = () => {
    takeScreenshot(snapshotRef.current).then((image: any) => {
      // Create a link to download the image
      const link = document.createElement("a");
      link.href = image;
      link.download = "statiegeld_drops_created_by_location.png"; // Set the default file name
      link.click(); // Programmatically click the link to trigger download
    });
  };

  /** Effects */
  useEffect(() => {
    getDropsStatsByLocation(period, date);
  }, [period, date]);

  return (
    <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl border-gray-border drop-shadow-none mb-1 sm:mb-10">
      <div className="flex flex-col sm:flex-row w-full items-start justify-between">
        <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black mb-12">
          Statiegeld drops by location
        </p>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end">
          {period === "weekly" ? (
            <div className="flex flex-col sm:flex-row items-start sm:items-center mr-0 sm:mr-[50px] mb-2 sm:mb-0">
              <PrimaryButton size="sm" onClick={getLastWeek} label="Prev" />
              <div className="mx-1 sm:mx-4 font-satoshi font-medium my-1 sm:my-0">
                {startOfWeek.toDateString()} - {endOfWeek.toDateString()}
              </div>
              <PrimaryButton
                size="sm"
                disabled={endOfWeek > new Date()}
                onClick={getNextWeek}
                label="Next"
              />
            </div>
          ) : null}
          <Menu>
            <MenuHandler>
              <button className="h-8 px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                {period.charAt(0).toUpperCase() + period.slice(1)}
                <ChevronDown className="ml-2" />
              </button>
            </MenuHandler>
            <MenuList placeholder={undefined}>
              <MenuItem
                disabled={period === "weekly"}
                onClick={() => {
                  setDate(new Date());
                  setPeriod("weekly");
                }}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Weekly
              </MenuItem>
              <MenuItem
                disabled={period === "monthly"}
                onClick={() => {
                  setDate(new Date());
                  setPeriod("monthly");
                }}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Monthly
              </MenuItem>
              <MenuItem
                disabled={period === "yearly"}
                onClick={() => {
                  setDate(new Date());
                  setPeriod("yearly");
                }}
                className="font-satoshi text-base text-dark"
                placeholder={undefined}
              >
                Yearly
              </MenuItem>
            </MenuList>
          </Menu>
          {period === "monthly" ? (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {dayjs(date).format("MMMM")}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem
                    key={month}
                    disabled={month === dayjs(date).month() + 1}
                    onClick={() =>
                      setDate(new Date(date.getFullYear(), month - 1, 15))
                    }
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {dayjs(new Date(date.getFullYear(), month - 1, 1)).format(
                      "MMMM"
                    )}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : null}
          {period === "monthly" || period === "yearly" ? (
            <Menu>
              <MenuHandler>
                <button className="h-8 px-3 ml-4 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-sm">
                  {date.getFullYear()}
                  <ChevronDown className="ml-2" />
                </button>
              </MenuHandler>
              <MenuList placeholder={undefined}>
                {Array.from(
                  { length: currentYear - 2023 },
                  (_, i) => currentYear - i
                ).map((year) => (
                  <MenuItem
                    key={year}
                    disabled={year === date.getFullYear()}
                    onClick={() =>
                      setDate(new Date(year, date.getMonth() - 1, 15))
                    }
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    {year}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          ) : null}
          <div className="ml-0 sm:ml-2" />
          <PrimaryButton
            size="sm"
            onClick={downloadScreenshot}
            label="Download image"
          />
        </div>
      </div>
      <div ref={snapshotRef}>
        {Object.values(dropsLocationStats ?? {}) ? (
          <Chart
            {...chartConfig}
            // @ts-ignore
            options={{
              ...chartConfig?.options,
              xaxis: {
                ...chartConfig?.options?.xaxis,
                categories: Object.keys(dropsLocationStats ?? {}),
              },
            }}
            series={[
              {
                name: "Statiegeld Drops created",
                data: Object.values(dropsLocationStats ?? {}),
              },
            ]}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <p className="mb-[100px] font-satoshi font-bold text-2xl text-tailgrids-black">
              No data found
            </p>
          </div>
        )}
      </div>
    </div>
  );
});

const chartConfig = {
  type: "bar",
  width: "100%",
  height: 240,
  options: {
    chart: {
      toolbar: {
        show: false,
      },
    },
    title: {
      show: "",
    },
    dataLabels: {
      enabled: false,
    },
    colors: ["#194128", "#377D51"],
    plotOptions: {
      bar: {
        columnWidth: 12,
        borderRadius: 2,
      },
    },
    xaxis: {
      axisTicks: {
        show: false,
      },
      axisBorder: {
        show: false,
      },
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: "#64748B",
          fontSize: "14px",
          fontFamily: "Satoshi",
          fontWeight: 500,
        },
      },
    },
    grid: {
      show: true,
      borderColor: "#dddddd",
      strokeDashArray: 5,
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
      padding: {
        top: 5,
        right: 0,
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      theme: "dark",
    },
  },
};
